import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, input, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25Util } from "../../../util/s25-util";
import { TrailApi } from "./trail.api";
import { TrailLabels } from "./trail.labels";

@TypeManagerDecorator("s25-ng-trail-experience-submit")
@Component({
    selector: "s25-ng-trail-experience-submit",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule],
    template: `
        <div class="trail-experience-submit">
            <h3 class="trail-experience-submit__title">{{ labels.SUBMIT_EXPERIENCE }}</h3>

            @if (submissionState() === "success") {
                <div class="trail-experience-submit__success">
                    <p>Your experience has been submitted for review.</p>
                    <button type="button" class="trail-experience-submit__close-btn" (click)="closed.emit()">
                        Close
                    </button>
                </div>
            } @else {
                <form class="trail-experience-submit__form" (ngSubmit)="submit()">
                    <div class="trail-experience-submit__field">
                        <label for="exp-title" class="trail-experience-submit__label">Title *</label>
                        <input
                            id="exp-title"
                            type="text"
                            class="trail-experience-submit__input"
                            required
                            [ngModel]="title()"
                            (ngModelChange)="title.set($event)"
                            name="title"
                        />
                    </div>

                    <div class="trail-experience-submit__field">
                        <label for="exp-desc" class="trail-experience-submit__label">Description *</label>
                        <textarea
                            id="exp-desc"
                            class="trail-experience-submit__textarea"
                            required
                            rows="4"
                            [ngModel]="description()"
                            (ngModelChange)="description.set($event)"
                            name="description"
                        ></textarea>
                    </div>

                    <div class="trail-experience-submit__field">
                        <label for="exp-date" class="trail-experience-submit__label">Experience Date *</label>
                        <input
                            id="exp-date"
                            type="date"
                            class="trail-experience-submit__input"
                            required
                            [ngModel]="experienceDate()"
                            (ngModelChange)="experienceDate.set($event)"
                            name="experienceDate"
                        />
                    </div>

                    @if (submissionState() === "error") {
                        <p class="trail-experience-submit__error">Unable to submit. Please try again.</p>
                    }

                    <div class="trail-experience-submit__actions">
                        <button
                            type="submit"
                            class="trail-experience-submit__submit-btn"
                            [disabled]="submitting() || !isFormValid()"
                        >
                            @if (submitting()) {
                                Submitting…
                            } @else {
                                {{ labels.SUBMIT_EXPERIENCE }}
                            }
                        </button>
                        <button type="button" class="trail-experience-submit__cancel-btn" (click)="closed.emit()">
                            Cancel
                        </button>
                    </div>
                </form>
            }
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .trail-experience-submit {
                padding: 1.25rem;
                background: var(--color--surface-2-bg);
                border: 1px solid var(--color--border);
                border-radius: 8px;
                max-width: 480px;
            }
            .trail-experience-submit__title {
                margin: 0 0 1rem;
                font-size: 1.1rem;
            }
            .trail-experience-submit__form {
                display: flex;
                flex-direction: column;
                gap: 0.75rem;
            }
            .trail-experience-submit__field {
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
            }
            .trail-experience-submit__label {
                font-size: 0.85rem;
                font-weight: 600;
            }
            .trail-experience-submit__input,
            .trail-experience-submit__textarea {
                padding: 0.4rem 0.6rem;
                border: 1px solid var(--color--border);
                border-radius: 4px;
                font-size: 0.9rem;
            }
            .trail-experience-submit__textarea {
                resize: vertical;
            }
            .trail-experience-submit__error {
                color: var(--color--danger);
                margin: 0;
                font-size: 0.85rem;
            }
            .trail-experience-submit__actions {
                display: flex;
                gap: 0.5rem;
                margin-top: 0.5rem;
            }
            .trail-experience-submit__submit-btn {
                padding: 0.45rem 1rem;
                border: none;
                border-radius: 4px;
                background: var(--cn-navy, #1e3a5f);
                color: #fff;
                font-size: 0.9rem;
                cursor: pointer;
                font-weight: 500;
            }
            .trail-experience-submit__submit-btn:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
            .trail-experience-submit__cancel-btn {
                padding: 0.45rem 1rem;
                border: 1px solid var(--color--border);
                border-radius: 4px;
                background: transparent;
                font-size: 0.9rem;
                cursor: pointer;
            }
            .trail-experience-submit__close-btn {
                padding: 0.45rem 1rem;
                border: 1px solid var(--color--border);
                border-radius: 4px;
                background: transparent;
                font-size: 0.9rem;
                cursor: pointer;
                margin-top: 0.75rem;
            }
            .trail-experience-submit__success {
                color: #155724;
            }
            .trail-experience-submit__success p {
                margin: 0 0 0.5rem;
            }
        `,
    ],
})
export class TrailExperienceSubmitComponent {
    readonly requirementId = input.required<number>();
    readonly defnId = input<number>();
    readonly trailId = input<number>();
    readonly closed = output<void>();
    readonly submitted = output<void>();

    readonly labels = TrailLabels;

    readonly title = signal("");
    readonly description = signal("");
    readonly experienceDate = signal("");
    readonly submitting = signal(false);
    readonly submissionState = signal<"idle" | "success" | "error">("idle");

    isFormValid(): boolean {
        return !!this.title().trim() && !!this.description().trim() && !!this.experienceDate();
    }

    async submit(): Promise<void> {
        if (!this.isFormValid() || this.submitting()) return;

        this.submitting.set(true);
        this.submissionState.set("idle");

        try {
            await TrailApi.submitExperience({
                requirementId: this.requirementId(),
                defnId: this.defnId(),
                trailId: this.trailId(),
                title: this.title().trim(),
                description: this.description().trim(),
                experienceDt: this.experienceDate(),
            });
            this.submissionState.set("success");
            this.submitted.emit();
        } catch (caughtError) {
            S25Util.showError(caughtError);
            this.submissionState.set("error");
        } finally {
            this.submitting.set(false);
        }
    }
}
